/**
 * formatMessage — Renders WhatsApp-style formatted text as React elements.
 *
 * Handles:
 * - *texto* → bold
 * - _texto_ → italic
 * - ~texto~ → strikethrough
 * - `code` → inline code
 * - ```block``` → code block
 * - Lines starting with "- " or "• " → bullet list items
 * - Numbered lines like "1. " → ordered list items
 * - Newlines → <br />
 *
 * The backend (markdownToWhatsapp.ts) already converts standard markdown
 * (**bold**, ## headings, etc.) to WhatsApp-style format before sending.
 * This function renders that format visually in the demo chat.
 */

import React from 'react';

/**
 * Parses WhatsApp-style formatted text and returns React elements.
 */
export function formatMessage(text: string): React.ReactNode {
  if (!text) return null;

  // Split by code blocks first (preserve them)
  const codeBlockParts = text.split(/(```[\s\S]*?```)/);

  const elements: React.ReactNode[] = [];

  for (let i = 0; i < codeBlockParts.length; i++) {
    const part = codeBlockParts[i];

    if (part.startsWith('```') && part.endsWith('```')) {
      // Code block
      const code = part.slice(3, -3).replace(/^\w*\n/, ''); // Remove optional language tag
      elements.push(
        <pre
          key={`code-${i}`}
          style={{
            background: 'rgba(0,0,0,0.06)',
            borderRadius: '8px',
            padding: '8px 12px',
            margin: '6px 0',
            fontSize: '12px',
            fontFamily: 'monospace',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {code.trim()}
        </pre>
      );
    } else {
      // Regular text — process lines
      elements.push(<FormattedText key={`text-${i}`} text={part} />);
    }
  }

  return <>{elements}</>;
}

/**
 * Renders a block of text with inline formatting and list detection.
 */
function FormattedText({ text }: { text: string }) {
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let listBuffer: { type: 'ul' | 'ol'; items: React.ReactNode[] } | null = null;

  function flushList() {
    if (!listBuffer) return;
    if (listBuffer.type === 'ul') {
      result.push(
        <ul key={`list-${result.length}`} style={{ margin: '4px 0', paddingLeft: '20px' }}>
          {listBuffer.items.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '2px' }}>{item}</li>
          ))}
        </ul>
      );
    } else {
      result.push(
        <ol key={`list-${result.length}`} style={{ margin: '4px 0', paddingLeft: '20px' }}>
          {listBuffer.items.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '2px' }}>{item}</li>
          ))}
        </ol>
      );
    }
    listBuffer = null;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Unordered list item: "- text" or "• text"
    const ulMatch = line.match(/^[\-•]\s+(.+)/);
    if (ulMatch) {
      if (!listBuffer || listBuffer.type !== 'ul') {
        flushList();
        listBuffer = { type: 'ul', items: [] };
      }
      listBuffer.items.push(formatInline(ulMatch[1]));
      continue;
    }

    // Ordered list item: "1. text", "2. text", etc.
    const olMatch = line.match(/^\d+\.\s+(.+)/);
    if (olMatch) {
      if (!listBuffer || listBuffer.type !== 'ol') {
        flushList();
        listBuffer = { type: 'ol', items: [] };
      }
      listBuffer.items.push(formatInline(olMatch[1]));
      continue;
    }

    // Not a list item — flush any pending list
    flushList();

    // Empty line → spacing
    if (line.trim() === '') {
      if (i > 0 && i < lines.length - 1) {
        result.push(<br key={`br-${i}`} />);
      }
      continue;
    }

    // Regular line with inline formatting
    result.push(
      <span key={`line-${i}`}>
        {i > 0 && result.length > 0 && <br />}
        {formatInline(line)}
      </span>
    );
  }

  flushList();

  return <>{result}</>;
}

/**
 * Applies inline formatting: *bold*, _italic_, ~strikethrough~, `code`, URLs
 */
function formatInline(text: string): React.ReactNode {
  // Process inline code first (protect from other formatting)
  const parts = text.split(/(`[^`]+`)/);

  const elements: React.ReactNode[] = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (part.startsWith('`') && part.endsWith('`')) {
      // Inline code
      elements.push(
        <code
          key={i}
          style={{
            background: 'rgba(0,0,0,0.06)',
            borderRadius: '3px',
            padding: '1px 4px',
            fontSize: '0.9em',
            fontFamily: 'monospace',
          }}
        >
          {part.slice(1, -1)}
        </code>
      );
    } else {
      // Apply bold, italic, strikethrough, and URLs
      elements.push(<InlineFormatted key={i} text={part} />);
    }
  }

  return <>{elements}</>;
}

/**
 * Renders text with *bold*, _italic_, ~strikethrough~ formatting and clickable URLs.
 * Image URLs (.jpg, .png, .webp, .gif) render as inline images.
 */
function InlineFormatted({ text }: { text: string }) {
  // First split by URLs
  const urlSplitRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/;
  const urlParts = text.split(urlSplitRegex);

  if (urlParts.length === 1) {
    // No URLs — just apply formatting
    return <>{applyTextFormatting(text)}</>;
  }

  return (
    <>
      {urlParts.map((part, i) => {
        if (/^https?:\/\//.test(part)) {
          // Check if it's an image URL
          const isImage = /\.(jpe?g|png|webp|gif|svg)(\?.*)?$/i.test(part);
          if (isImage) {
            return (
              <img
                key={i}
                src={part}
                alt="Producto"
                loading="lazy"
                style={{
                  maxWidth: '100%',
                  width: '200px',
                  borderRadius: '8px',
                  display: 'block',
                  marginTop: '6px',
                  marginBottom: '6px',
                }}
              />
            );
          }
          // Regular URL — render as clickable link
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1a73e8', textDecoration: 'underline', wordBreak: 'break-all' }}
            >
              {part.length > 50 ? `${part.slice(0, 47)}...` : part}
            </a>
          );
        }
        // Regular text — apply formatting
        return <React.Fragment key={i}>{applyTextFormatting(part)}</React.Fragment>;
      })}
    </>
  );
}

/**
 * Applies *bold*, _italic_, ~strikethrough~ to text without URLs.
 */
function applyTextFormatting(text: string): React.ReactNode {
  const regex = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/;
  const parts = text.split(regex);

  if (parts.length === 1) {
    return <>{text}</>;
  }

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
          return <strong key={i}>{part.slice(1, -1)}</strong>;
        }
        if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
          return <em key={i}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('~') && part.endsWith('~') && part.length > 2) {
          return <s key={i}>{part.slice(1, -1)}</s>;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}
